import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Printer, Trophy, Award, Swords, Sparkles } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Report() {
  const { settings } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/report?year=${year}`);
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [year]);

  const years = [];
  for (let y = currentYear + 1; y >= currentYear - 4; y--) years.push(y);

  const exportPDF = () => window.print();

  return (
    <div className="space-y-8 print-container">
      <div className="no-print flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
            Récap saison
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
            Bilan annuel
          </h1>
        </div>
        <div className="flex gap-2">
          <select
            data-testid="year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm font-bold uppercase tracking-wide"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            data-testid="print-report-button"
            onClick={exportPDF}
            className="inline-flex items-center gap-2 px-4 py-3 bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide rounded-lg text-sm"
          >
            <Printer className="w-4 h-4" /> Export PDF
          </button>
        </div>
      </div>

      <div className="print-only mb-6">
        <div className="text-3xl font-bold">{settings.app_name || "ASS les Régionnaires"}</div>
        <div className="text-lg">Bilan de la saison {year}</div>
      </div>

      {loading || !data ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : data.matches_count === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          Aucun match joué en {year}
        </div>
      ) : (
        <>
          <div className="rounded-2xl bg-slate-900 border border-white/10 p-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <Cell label="Matchs joués" value={data.matches_count} color="#E5B85C" />
              <Cell label="Bleus V" value={data.duel.bleus_wins} color="#60A5FA" />
              <Cell label="Nuls" value={data.duel.draws} color="#94A3B8" />
              <Cell label="Rouges V" value={data.duel.rouges_wins} color="#DC2626" />
            </div>
            <div className="grid grid-cols-2 gap-4 text-center mt-4">
              <Cell label="Buts marqués Bleus" value={data.duel.bleus_goals} color="#60A5FA" small />
              <Cell label="Buts marqués Rouges" value={data.duel.rouges_goals} color="#DC2626" small />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center gap-2 bg-[#E5B85C]/10">
                <Trophy className="w-4 h-4 text-[#E5B85C]" />
                <div className="font-display font-bold text-sm uppercase">Top buteurs</div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {data.top_scorers.length === 0 && (
                    <tr>
                      <td className="p-4 text-center text-slate-500 text-sm">Aucun buteur</td>
                    </tr>
                  )}
                  {data.top_scorers.map((p, i) => (
                    <tr key={p.player_id} className="border-b border-white/5">
                      <td className="p-3 font-mono-stat text-slate-500 w-10">{i + 1}</td>
                      <td className="p-3 font-bold">{p.player_name}</td>
                      <td className="p-3 text-right font-mono-stat font-bold text-[#E5B85C]">
                        {p.goals}
                      </td>
                      <td className="p-3 text-right font-mono-stat text-sky-400 w-16">
                        {p.assists}A
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center gap-2 bg-[#E5B85C]/10">
                <Award className="w-4 h-4 text-[#E5B85C]" />
                <div className="font-display font-bold text-sm uppercase">Rois MVP</div>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {data.mvp_ranking.length === 0 && (
                    <tr>
                      <td className="p-4 text-center text-slate-500 text-sm">Aucun MVP décerné</td>
                    </tr>
                  )}
                  {data.mvp_ranking.map((p, i) => (
                    <tr key={p.player_id} className="border-b border-white/5">
                      <td className="p-3 font-mono-stat text-slate-500 w-10">{i + 1}</td>
                      <td className="p-3 font-bold">{p.player_name}</td>
                      <td className="p-3 text-right font-mono-stat font-bold text-[#E5B85C]">
                        {p.count}×
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Palmarès Gala */}
          {data.gala && data.gala.matches_count > 0 && (
            <GalaPalmares gala={data.gala} />
          )}

          <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-2 bg-slate-800">
              <Swords className="w-4 h-4 text-slate-400" />
              <div className="font-display font-bold text-sm uppercase">
                Journal des matchs · {data.matches.length}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 border-b border-white/5">
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Titre</th>
                    <th className="p-3 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {data.matches.map((m) => {
                    const isGala = m.match_type === "gala";
                    const isOfficial = m.match_type === "official";
                    let title;
                    if (isGala) title = m.title || `Gala · ${m.team_a_name || "A"} vs ${m.team_b_name || "B"}`;
                    else if (isOfficial) title = `${m.title || "Officiel"} · vs ${m.opponent_name || "Adv."}`;
                    else title = m.title || "Match ASS les Régionnaires";
                    return (
                      <tr key={m.id} className="border-b border-white/5">
                        <td className="p-3 font-mono-stat text-slate-400">{formatDate(m.match_date)}</td>
                        <td className="p-3">
                          {isGala && (
                            <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 mr-2">
                              Gala
                            </span>
                          )}
                          {isOfficial && (
                            <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#E5B85C]/20 text-[#E5B85C] mr-2">
                              Officiel
                            </span>
                          )}
                          {title}
                        </td>
                        <td className="p-3 text-right font-mono-stat font-bold whitespace-nowrap">
                          {isGala ? (
                            <>
                              <span className="text-purple-300">{m.team_a_name || "A"} {m.score_team_a ?? 0}</span>
                              <span className="text-slate-500 mx-1">-</span>
                              <span className="text-fuchsia-300">{m.score_team_b ?? 0} {m.team_b_name || "B"}</span>
                            </>
                          ) : isOfficial ? (
                            <>
                              <span className="text-[#E5B85C]">Nous {m.our_score ?? 0}</span>
                              <span className="text-slate-500 mx-1">-</span>
                              <span className="text-slate-300">{m.opponent_score ?? 0}</span>
                            </>
                          ) : (
                            <>
                              <span className="text-sky-400">B {m.score_bleus ?? 0}</span>
                              <span className="text-slate-500 mx-1">-</span>
                              <span className="text-rose-400">{m.score_rouges ?? 0} R</span>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ label, value, color, small }) {
  return (
    <div>
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div
        className={`font-display font-black ${small ? "text-3xl" : "text-4xl"} mt-1`}
        style={{ color }}
      >
        {value}
      </div>
    </div>
  );
}

function GalaPalmares({ gala }) {
  return (
    <div
      data-testid="gala-palmares"
      className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden"
    >
      <div className="p-4 border-b border-white/10 flex items-center gap-2 bg-purple-500/10">
        <Sparkles className="w-4 h-4 text-purple-300" />
        <div className="font-display font-bold text-sm uppercase">
          Palmarès Gala · {gala.matches_count} rencontre{gala.matches_count > 1 ? "s" : ""}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-5 border-b border-white/10">
        <Cell label="Galas joués" value={gala.matches_count} color="#A855F7" small />
        <Cell label="Buts totaux" value={gala.total_goals} color="#D946EF" small />
        <Cell
          label="Meilleure équipe"
          value={gala.top_teams[0]?.team_name || "—"}
          color="#E5B85C"
          small
        />
      </div>

      {gala.top_teams.length > 0 && (
        <div className="p-4 border-b border-white/10">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-3">
            Podium des victoires
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {gala.top_teams.slice(0, 3).map((t, i) => (
              <div
                key={t.team_name + i}
                data-testid={`gala-top-team-${i}`}
                className="p-3 rounded-lg bg-slate-800/60 border border-white/5 flex items-center gap-3"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center font-mono-stat font-black text-sm"
                  style={{
                    background: i === 0 ? "#E5B85C" : i === 1 ? "#94A3B8" : "#B45309",
                    color: "#0B0F17",
                  }}
                >
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate text-sm">{t.team_name}</div>
                  <div className="text-[10px] font-mono-stat uppercase text-slate-400">
                    {t.wins} victoire{t.wins > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 border-b border-white/5">
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Affiche</th>
              <th className="p-3 text-right">Score</th>
              <th className="p-3 text-left">Vainqueur</th>
            </tr>
          </thead>
          <tbody>
            {gala.matches.map((g) => (
              <tr
                key={g.id}
                data-testid={`gala-palmares-row-${g.id}`}
                className="border-b border-white/5"
              >
                <td className="p-3 font-mono-stat text-slate-400 whitespace-nowrap">
                  {formatDate(g.match_date)}
                </td>
                <td className="p-3">
                  {g.title ? (
                    <>
                      <span className="font-bold">{g.title}</span>
                      <span className="text-slate-500"> · </span>
                    </>
                  ) : null}
                  <span className="text-purple-300">{g.team_a_name}</span>
                  <span className="text-slate-500"> vs </span>
                  <span className="text-fuchsia-300">{g.team_b_name}</span>
                  {g.guests_count > 0 && (
                    <span className="ml-2 text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-white/10">
                      {g.guests_count} invité{g.guests_count > 1 ? "s" : ""}
                    </span>
                  )}
                </td>
                <td className="p-3 text-right font-mono-stat font-bold whitespace-nowrap">
                  <span className="text-purple-300">{g.score_team_a}</span>
                  <span className="text-slate-500 mx-1">-</span>
                  <span className="text-fuchsia-300">{g.score_team_b}</span>
                </td>
                <td className="p-3">
                  {g.winner ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E5B85C]/15 border border-[#E5B85C]/40 text-[#E5B85C] text-[10px] font-bold uppercase tracking-widest">
                      <Trophy className="w-3 h-3" /> {g.winner}
                    </span>
                  ) : (
                    <span className="text-slate-500 text-xs font-mono-stat uppercase">Match nul</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
